import { has, head, keys, pick } from 'lodash/fp';
import validate from 'validate.js';

import { FeatureFlag } from '@mews-distributor/common';

import { getCollectionIds } from '@mews/fp';
import { BusinessError, createAction, logSentryWarning, parsePaymentCardExpirationDate } from '@mews/framework';

import {
    ConfirmationScreenMandatoryQueryData,
    prepareCheckout,
    preserveConfirmationParams,
    showCheckout,
    showConfirmation,
    showSummary,
} from '../../../../actions/navigation';
import { createReservationGroup } from '../../../../api/actions/reservationGroups';
import { fetchFailedExchangeRates } from '../../../../modules/entities/exchangeRates/actions';
import { shouldAuthorizePaymentCard } from '../../../../modules/entities/paymentCards/actions';
import { getSelectedHotelId } from '../../../../modules/resourceOrder/selectors';
import { pciProxy } from '../../../../services/pciProxy';

import { DistributorThunkAction } from '../../../../types';
import { mapWithIndexes } from '../../../../utils/func';
import { CHECKOUT_PREVIOUS_STEP_SET } from '../../../confirmation/data/actionTypes';
import { redirectToPaymentGatewayCardAuthorization, redirectToPaymentGatewayPaymentRequest } from '../../data/actions';
import { getCustomerValues, isCheckoutFetching } from '../../data/selectors';
import { shouldCheckoutPreparationStart, shouldDisplayPaymentCardForm } from '../../data/viewSelectors';
import { CHECKOUT_REQUEST_STARTED, CHECKOUT_REQUEST_SUCCEEDED } from './actionTypes';
import {
    areAllFieldsTouched,
    getCustomerValue,
    getCustomerValuesConstraints,
    getCustomerValuesSchema,
    hasCheckoutFailed,
    hasCustomerErrors,
} from './selectors';
import {
    cardEncryptionSuccess,
    clearCustomerErrors,
    confirmReservation,
    failCardEncryption,
    failCheckoutRequest,
    setCustomerValue,
    startCardEncryption,
    touchAllFields,
    validateCustomerValues,
} from './slice';
import { CheckoutCustomerFormValues } from './types';

export function checkoutTryAgain() {
    return (dispatch: $TSFixMe, getState: $TSFixMe) => {
        const state = getState();
        dispatch(fetchFailedExchangeRates());
        if (shouldCheckoutPreparationStart(state)) {
            dispatch(prepareCheckout());
        } else if (hasCheckoutFailed(state)) {
            dispatch(finishBooking());
        }
    };
}

export function checkoutDismiss() {
    return (dispatch: $TSFixMe, getState: $TSFixMe) => {
        const state = getState();

        if (shouldCheckoutPreparationStart(state)) {
            dispatch(showSummary());
        } else if (hasCheckoutFailed(state)) {
            dispatch(showCheckout());
        }
    };
}

export function changeCustomerValue({ key, value }: $TSFixMe): DistributorThunkAction {
    return (dispatch) => {
        dispatch(setCustomerValue({ key, value }));
        dispatch(validateField(key));
    };
}

const validateField =
    (key: keyof CheckoutCustomerFormValues): DistributorThunkAction =>
    (dispatch, getState, { featureFlagService }) => {
        const state = getState();
        const isPhoneInputPrototypeEnabled = featureFlagService.hasFlag(FeatureFlag.enablePhoneInputPrototype);
        const isZodValidationEnabled = featureFlagService.hasFlag(FeatureFlag.zodValidationEnabled);

        const value = getCustomerValue(state, { name: key });

        if (isZodValidationEnabled) {
            const schema = getCustomerValuesSchema(state, {});
            // @ts-expect-error
            const fieldSchema = schema.shape[key];

            const { error } = fieldSchema.safeParse(value);
            // eslint-disable-next-line @typescript-eslint/strict-boolean-expressions
            if (error) {
                dispatch(validateCustomerValues([{ key, error: error.message }]));
            }
        } else {
            const constraints = getCustomerValuesConstraints(state, { isPhoneInputPrototypeEnabled });

            // @ts-expect-error $TSFixMeError: Element implicitly has an 'any' type because expression of type 'any' can't be used to index type '{ bookerEmail?: { pre...
            // eslint-disable-next-line @typescript-eslint/strict-boolean-expressions
            if (constraints[key]) {
                // @ts-expect-error $TSFixMeError: Element implicitly has an 'any' type because expression of type 'any' can't be used to index type '{ bookerEmail?: { pre...
                const error = head(validate.single(value, constraints[key]));
                dispatch(validateCustomerValues([{ key, error }]));
            }
        }
    };

export const handleBookingOwnerChange = () => (dispatch: $TSFixMe, getState: $TSFixMe) => {
    if (areAllFieldsTouched(getState())) {
        dispatch(validateAllValues());
    } else {
        dispatch(validateFieldsWithValues());
    }
};

const validateValues =
    ({ values, constraints }: $TSFixMe) =>
    (dispatch: $TSFixMe) => {
        const validationErrors = validate(values, constraints, { fullMessages: false });
        const errors = mapWithIndexes(
            // @ts-expect-error $TSFixMeError: Parameter 'e' implicitly has an 'any' type.
            (e, key) => ({
                key,
                error: head(e),
            }),
            validationErrors
        );
        dispatch(clearCustomerErrors());
        dispatch(validateCustomerValues(errors));
    };

const validateFieldsWithValues =
    (): DistributorThunkAction =>
    (dispatch, getState, { featureFlagService }) => {
        const state = getState();
        const values = getCustomerValues(state);
        const isPhoneInputPrototypeEnabled = featureFlagService.hasFlag(FeatureFlag.enablePhoneInputPrototype);

        const constraints = getCustomerValuesConstraints(state, { isPhoneInputPrototypeEnabled });

        const valuesConstraints = pick(keys(values), constraints);

        dispatch(validateValues({ values, constraints: valuesConstraints }));
    };

const validateAllValues =
    (): DistributorThunkAction =>
    (dispatch, getState, { featureFlagService }) => {
        const state = getState();
        const values = getCustomerValues(state);
        const isPhoneInputPrototypeEnabled = featureFlagService.hasFlag(FeatureFlag.enablePhoneInputPrototype);

        const constraints = getCustomerValuesConstraints(state, { isPhoneInputPrototypeEnabled });

        dispatch(validateValues({ values, constraints }));
    };

const checkoutRequestStartedAction = createAction(CHECKOUT_REQUEST_STARTED);
const setCheckoutPreviousStepAction = createAction(CHECKOUT_PREVIOUS_STEP_SET);

const checkoutShowConfirmationActionCreator =
    ({
        reservationGroupId,
        enterpriseId,
        reservationIds,
    }: Partial<ConfirmationScreenMandatoryQueryData> = {}): DistributorThunkAction =>
    (dispatch) => {
        dispatch(preserveConfirmationParams({ reservationGroupId, enterpriseId, reservationIds }));
        dispatch(showConfirmation());
        dispatch(confirmReservation());
    };

const afterReservationActionCreator =
    ({ enterpriseId, paymentCardId, paymentRequestId, reservationGroupId, reservationIds }: $TSFixMe) =>
    async (dispatch: $TSFixMe, _getState: $TSFixMe, { loggerService }: $TSFixMe) => {
        // eslint-disable-next-line @typescript-eslint/strict-boolean-expressions
        if (paymentRequestId) {
            dispatch(redirectToPaymentGatewayPaymentRequest({ paymentRequestId, reservationGroupId, reservationIds }));
            return;
        }

        // eslint-disable-next-line @typescript-eslint/strict-boolean-expressions
        const shouldAuthorizeCard = paymentCardId
            ? await dispatch(shouldAuthorizePaymentCard({ enterpriseId, paymentCardId }))
            : false;

        loggerService.log('payment-card-authorize-result', 'Result for 3DS redirect to card authorization page', {
            paymentCardId,
            enterpriseId,
            reservationGroupId,
            shouldAuthorizeCard,
        });

        // eslint-disable-next-line @typescript-eslint/strict-boolean-expressions
        if (shouldAuthorizeCard) {
            dispatch(
                redirectToPaymentGatewayCardAuthorization({
                    paymentCardId,
                    reservationGroupId,
                    reservationIds,
                })
            );
            return;
        }

        dispatch(setCheckoutPreviousStepAction);
        dispatch(checkoutShowConfirmationActionCreator({ enterpriseId, reservationGroupId, reservationIds }));
    };

export const finishBooking = (): DistributorThunkAction => async (dispatch, getState) => {
    dispatch(touchAllFields());
    dispatch(validateAllValues());

    const state = getState();

    if (!hasCustomerErrors(state) && !isCheckoutFetching(state)) {
        const enterpriseId = getSelectedHotelId(state);

        dispatch(checkoutRequestStartedAction);

        try {
            const creditCardData = await dispatch(getCreditCardData());
            const {
                id: reservationGroupId,
                paymentRequestId,
                paymentCardId,
                reservations,
            } = await dispatch(createReservationGroup({ enterpriseId, creditCardData }));

            dispatch(createAction(CHECKOUT_REQUEST_SUCCEEDED));

            try {
                await dispatch(
                    afterReservationActionCreator({
                        enterpriseId,
                        reservationGroupId,
                        paymentRequestId,
                        paymentCardId,
                        reservationIds: getCollectionIds(reservations),
                    })
                );
            } catch (error) {
                // Errors at this point are not expected and shouldn't affect the user flow, so try showing confirmation screen to the user.
                dispatch(checkoutShowConfirmationActionCreator());
                dispatch(
                    logSentryWarning({
                        error,
                        extra: {
                            step: 'finishBooking - after reservation confirmation',
                        },
                    })
                );
            }
        } catch (error) {
            if (has(['data', 'ExceedingReservationIndexes'], error)) {
                dispatch(showSummary());
            }
            dispatch(failCheckoutRequest({ error }));
        }
    }
};

const getCreditCardData = () => (dispatch: $TSFixMe, getState: $TSFixMe) => {
    const state = getState();

    // without payment card form, resolve to empty data
    if (!shouldDisplayPaymentCardForm(state, {})) {
        return Promise.resolve({
            paymentGatewayData: null,
            obfuscatedCreditCardNumber: null,
        });
    }

    return dispatch(getPciProxyCreditCardData());
};

const getPciProxyCreditCardData =
    () =>
    (dispatch: $TSFixMe, getState: $TSFixMe, { loggerService }: $TSFixMe) => {
        const t = (message: $TSFixMe, params = {}) => loggerService.info('checkout-trace', message, params);

        t('Checkout started');
        const state = getState();
        const customerValues = getCustomerValues(state);
        t('Customer values', customerValues);

        t('Parsing expiration');
        const expirationValue = getCustomerValue(state, { name: 'expiration' });
        // @ts-expect-error $TSFixMeError: Argument of type 'string | undefined' is not assignable to parameter of type 'string'.
        const { year, month } = parsePaymentCardExpirationDate(expirationValue);
        t('Expiration parsed', { year, month });
        const expiration = `${year}-${month}`;
        dispatch(startCardEncryption());
        t('Tokenization started');

        return dispatch(pciProxy.actions.tokenizeCard())
            .then((nonce: $TSFixMe) => {
                t('Tokenization successful');
                dispatch(cardEncryptionSuccess());
                const holderName = getCustomerValue(state, { name: 'holderName' });
                return {
                    paymentGatewayData: nonce,
                    holderName,
                    expiration,
                };
            })
            .catch((error: $TSFixMe) => {
                t('Tokenization failed', { error });
                // eslint-disable-next-line @typescript-eslint/strict-boolean-expressions
                const message = (error && error.message) || error;
                const businessError = new BusinessError(message);

                dispatch(failCardEncryption({ error: businessError }));
                throw businessError;
            });
    };
